import React, {createRef, useEffect, useRef, useState} from "react";
import cookie from "react-cookies";
import {
    Alert,
    Button,
    Carousel,
    Col,
    Dropdown,
    Empty,
    Icon,
    Menu,
    message,
    Popconfirm,
    Progress,
    Row,
    Spin,
    Tooltip,
} from 'antd';

import {
    BuildOutlined,
    ClockCircleOutlined,
    CloudSyncOutlined,
    DeleteFilled,
    EditOutlined,
    ExclamationCircleOutlined,
    ExclamationCircleTwoTone,
    DownloadOutlined,
    ExportOutlined,
    FileDoneOutlined,
    FileExclamationTwoTone,
    FolderOutlined,
    HourglassOutlined,
    InfoCircleOutlined,
    LeftOutlined,
    LoadingOutlined,
    PlayCircleOutlined,
    RightOutlined,
    SolutionOutlined,
    StarFilled,
    UploadOutlined,
    UsergroupAddOutlined
} from '@ant-design/icons';
import {cancelExport, prioritizeExport, startExport, startTiling} from '../../redux_actions/slides.action'
import {get} from '../../helper/request';
import EditableTagGroup from '../viewer/viewer.tags';
import {
    getBarcodeUrl,
    getPreviewUrl,
    getRealRemoteURL,
    isCloud,
    isRunningFromSite,
    isSuperuser,
    isTrue,
    notNull,
    getStateAwareUrl,
    openEndPoint, slideStatusCategory
} from '../../utils/utils';
import {
    ArchiveConstants,
    defaultBarcode,
    defaultPreview,
    objectiveType,
    slideExportStatus, slideStatusEnum,
    slideUploadStatus,
    slideViewType
} from '../../utils/const';
import {FaCircle} from "react-icons/all";
import {getSlideArchivalDate, patchSlide} from "../../utils/slide_utils";
import {Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import {ErrorTwoTone} from "@mui/icons-material";
import CircularProgressWithLabel from "../gammaviewer/components/CircularProgressWithLabel";
import {addSlidesToShare} from "../../redux_actions/shareManager.action";
import {getCaseNamePlaceholder} from "utils/org_utils";
import {aclPermissions, hasPermissions} from "const/ACLPermissions";
import { tileViewApp } from "component/gammaviewer/app_maker";

const iconFontSize = "15px";

const unitToShortMap = {
    "MILLISECONDS": "ms",
    "SECONDS": "sec",
    "MINUTES": "min",
    "MICRONS": "um",
    "MILLIMETERS": "mm",
};

export const shortenUnit = (unit) => {
    return (unit in unitToShortMap) ? unitToShortMap[unit] : '';
}

export const getUnreachableComponent = (slide) =>
    <Stack direction={"column"} alignItems={"center"} justifyContent={"center"} sx={{height: "100%"}}>
        <ErrorTwoTone sx={{fontSize: "50px", color: "#ff6666"}}/>
        <Typography variant={"subtitle2"} color={"#ff0000"}>
            {slide.device_ip} is unreachable
        </Typography>
    </Stack>

export const getPreviewComponent = (slide, is_cloud, isMobile) => {
    let previewPath = slide.preview_image;

    if (is_cloud) {
        let parts = previewPath.split("/");
        if (parts.length >= 2) {
            previewPath = `${parts[parts.length-2]}/${parts[parts.length-1]}`;
        }
        previewPath = `${slide.cloud_url}/${slide.path}${previewPath}`;
    }

    if (is_cloud === false) {
        previewPath = previewPath.replace("/static/", "");
        previewPath = getPreviewUrl(previewPath.replace('stitched_small_viewer', 'selection_marked'), slide);
    }
    return <img
        width={isMobile ? 100 : 200}
        height={isMobile ? 48 : 96}
        alt="logo"
        src={previewPath}
        onError={({event, currentTarget}) => {
            currentTarget.src = defaultPreview;
            if (notNull(event?.currentTarget?.onerror)) {
                event.currentTarget.onerror = null; // prevents looping
            }
        }}
    />
}

export const getLabelComponent = (slide, is_cloud, isMobile, rotate) => {
    const labelPath = getBarcodeUrl(slide, is_cloud);

    return <img
        style={{borderWidth: "2px", borderColor: "red"}}
        height={isMobile ? 54 : 108}
        width={isMobile ? 48 : 96}
        alt="barcode-does-not-exist"
        src={labelPath}
        onError={({event, currentTarget}) => {
            currentTarget.src = defaultBarcode;
            if (notNull(event?.currentTarget?.onerror)) {
                event.currentTarget.onerror = null; // prevents looping
            }
        }}
        className={rotate ? "rotate270" : ""}
    />
}

export const LabelComponentWithRotation = ({slide, is_cloud, isMobile}) => {

    let [rotationAngle, setRotationAngle] = useState(270);

    const labelPath = getBarcodeUrl(slide, is_cloud);
    return <Tooltip placement="rightTop" title={"Rotate Barcode"}>

        <div className="barcode-filled" onClick={() => {
            setRotationAngle((rotationAngle + 90) % 360);
        }}>
            <img key={Math.random()} className="barcode-image"
                 src={labelPath}
                 alt={"Barcode"}
                 style={{transform: 'rotate(' + rotationAngle + 'deg)'}}/>

            <div className="rotate"><Icon type="reload"/></div>
        </div>
    </Tooltip>
}

export const getAssignCaseComponent = (slide, handleAssignCase) => {
    if (!hasPermissions([aclPermissions.update_slides_name]) &&
        !hasPermissions([aclPermissions.update_slides_in_case])) {
        return null
    }

    return (
        <Tooltip placement="bottomRight" title={`Edit ${getCaseNamePlaceholder()} / Slide ID`}>
            <EditOutlined
                width={"28px"}
                fill="current"
                stroke="current"
                strokeWidth={"16px"}
                onClick={(event) => {
                    event.stopPropagation();
                    handleAssignCase(slide, !slide.barcode_failed);
                }}
                className="icon-hover slide-icons"/>
        </Tooltip>
    );
}

export const getSlideReadStatusComponent = (slide, handleSlideReadStatus) => {
    if (!isTrue((cookie.load("organisation_setting") || {})["make_slide_read_config"]) || !hasPermissions([aclPermissions.share_slide])) {
        return null
    }
    return (
        <Tooltip placement="bottomRight" title={`Slide Read Info`}>
            <InfoCircleOutlined
                width={"28px"}
                fill="current"
                stroke="current"
                strokeWidth={"16px"}
                onClick={(event) => {
                    event.stopPropagation();
                    handleSlideReadStatus(slide);
                }}
                className="icon-hover slide-icons"/>
        </Tooltip>
    )
}

export const getAssignCaseButtonComponent = (slide, handleAssignCase) => {
    if (!hasPermissions([aclPermissions.update_slides_in_case])) return null;
    return (
        <Button
            className="lighter-danger-button"
            type="danger"
            size='small'
            onClick={(event) => {
                event.stopPropagation();
                handleAssignCase(slide, !slide.barcode_failed);
            }}
            ghost
        >
            <ExclamationCircleOutlined style={{color: 'red', fontSize: '15px'}}/>
            Add to {getCaseNamePlaceholder()}
        </Button>
    );
}

export const getAssignCaseButtonComponent2 = (slide, handleAssignCase, set) => {
    return (
        <Button
            className="lighter-danger-button"
            type="danger"
            size='small'
            style={!set ? {visibility: "hidden"} : {visibility: "visible"}}
            onClick={(event) => {
                event.stopPropagation();
                handleAssignCase(slide, !slide.barcode_failed);
            }}
            ghost
        >
            <ExclamationCircleOutlined style={{color: 'red', fontSize: '15px'}}/>
            Add to {getCaseNamePlaceholder()}
        </Button>
    );
}

export const stitchingPercentComponent = (slide) => {
    if (slideStatusCategory(slide.status) === slideStatusEnum.SCANNING || slideStatusCategory(slide.status) === slideStatusEnum.VIEWER_READY) {
        return null;
    } else {
        return <div>
            <Progress percent={slide.stitcher_percent} type="circle" width={45} className="custom-font-family"/>
        </div>;
    }
}

export const getUploadComponent = (slide, handleUpload, handleDebugUpload) => {

    if (isCloud()) return null;

    var uploadStatus = slide.upload_status;

    var uploadComponent = null;

    if (!hasPermissions([aclPermissions.upload_slide])) {
        return null
    }

    if (slide.isFetching) {
        return getBusyComponent();
    }

    if (slideStatusCategory(slide.status) === slideStatusEnum.VIEWER_READY) {

        let adminMenu = <Menu>
            <Menu.Item>
                <Button onClick={(event) => {
                    event.stopPropagation();
                    handleUpload(slide.morphle_id, slide.id, false, false);
                }}>Upload without Debug</Button>
            </Menu.Item>
            <Menu.Item>
                <Button onClick={(event) => {
                    event.stopPropagation();
                    handleUpload(slide.morphle_id, slide.id, true, false);
                }}>Upload with Debug</Button>
            </Menu.Item>
            <Menu.Item>
                <Button onClick={(event) => {
                    event.stopPropagation();
                    handleDebugUpload(slide.morphle_id, slide.id, true, false);
                }}>Upload Only Debug</Button>
            </Menu.Item>
        </Menu>

        let adminUploadButton = <Dropdown key="admin-upload" overlay={adminMenu} placement="topLeft">
            <UploadOutlined
                key="cloud-upload"
                onClick={(event) => {
                    event.stopPropagation()
                }}
                className="icon-hover slide-icons"/>
        </Dropdown>

        let uploadButton = <Tooltip title="Upload">
            <UploadOutlined
                key="cloud-upload"
                onClick={(event) => {
                    event.stopPropagation();
                    handleUpload(slide.morphle_id, slide.id, false, false);
                }}
                className="icon-hover slide-icons"/>
        </Tooltip>

        if (uploadStatus === slideUploadStatus.NOT_STARTED) {
            uploadComponent = cookie.loadAll().superuser === "true" ? adminUploadButton : uploadButton
        } else if (uploadStatus === 2) {
            uploadComponent = <Tooltip title="Uploaded">
                <CloudSyncOutlined
                    style={{cursor: 'default'}}
                    className="slide-icons"
                    onClick={(event) => {
                        event.stopPropagation();
                    }}/>
            </Tooltip>
        } else if (uploadStatus === slideUploadStatus.ERROR || uploadStatus === slideUploadStatus.LIMIT_BREACH) {
            uploadComponent = <Tooltip title={uploadStatus === slideUploadStatus.LIMIT_BREACH ? "Upload limit reached" : "Upload Error"}>
                <ExclamationCircleTwoTone
                    twoToneColor='red'
                    style={{cursor: 'pointer'}}
                    className="icon-hover slide-icons"
                    onClick={(event) => {
                        event.stopPropagation();
                        handleUpload(slide.morphle_id, slide.id, false, false);
                    }}/>
            </Tooltip>
        } else if (uploadStatus === slideUploadStatus.IN_PROGRESS) {
            if (slide.upload_percent <= 0) {
                uploadComponent = <Tooltip title="Queued for upload">
                    <HourglassOutlined
                        style={{color: "#40a9ff", cursor: 'default'}}
                        className="slide-icons"
                        onClick={(event) => {
                            event.stopPropagation();
                        }}/>
                </Tooltip>
            } else {
                uploadComponent = <CircularProgressWithLabel key={"3"} value={slide.upload_percent}/>
            }
        }
    } else {
        uploadComponent = <div>Scanning</div>
    }

    return uploadComponent;
}

export const getDbEntryComponent = (slide) => {
    if (!isSuperuser()) return null;
    return <Tooltip placement="bottomRight" title="DB Entry">
        <SolutionOutlined
            onClick={(event) => {
                event.stopPropagation();
                let localPath = "/admin/api/slide/" + slide.id + "/change/";
                if (isRunningFromSite()) {
                    window.open(getRealRemoteURL(localPath), '_blank')
                } else {
                    openEndPoint(localPath, true, false);
                }
            }}
            className="icon-hover slide-icons"/>
    </Tooltip>;
}

export const getMorphleIDComponent = (slide) => {
    if (isCloud() || !isSuperuser()) return null;
    let localPath = "app://" + slide.loc_on_machine + "/scans/" + slide.bucket_name + "/" + slide.path;
    return <Tooltip placement="bottomRight" title={slide.morphle_id}>
        <FolderOutlined
            onClick={(event) => {
                event.stopPropagation();
                openEndPoint(localPath, false, false);
            }}
            className="icon-hover slide-icons"/>
    </Tooltip>;
}

export const getExecutableComponent = (slide) => {
    let localPath = "file:///" + slide.loc_on_machine + "/scans/" + slide.bucket_name + "/" + slide.path + "debug/zprofile/plane3d.html";
    console.log(localPath);
    return <Tooltip placement="bottomRight" title={slide.morphle_id}>
        <Icon
            onClick={(event) => {
                event.stopPropagation();
                openEndPoint(localPath, false, false);
            }}
            type="area-chart"
            className="icon-hover slide-icons"
        />
    </Tooltip>;
}

var getBusyComponent = () => {
    let antIcon = <LoadingOutlined style={{fontSize: iconFontSize}} spin/>;
    return <Spin indicator={antIcon}/>
}

export const getTagsComponent = (slide, dispatch, urlState, isMobile = false) => {
    return <div onClick={(e) => e.stopPropagation()} className='tags-forTour'>
        <EditableTagGroup tag={slide.tags}
                          edit={!isMobile}
                          morphle_id={slide.morphle_id}
                          path={slide.path}
                          date={slide.date}
                          specimen_type={slide.specimen_type}
                          slide_id={slide.id}
                          archival_status={slide.archive_status}
        />
    </div>
}

export const getStarredComponent = (slide, handleStarButton) => {
    let icon = <Tooltip placement={"bottomRight"} title={"Bookmark"}>
        <StarFilled
            onClick={(event) => {
                event.stopPropagation();
                handleStarButton(slide, !slide.starred);
            }}
            style={{color: slide.starred === true ? "#f5667b" : ""}}
            className="icon-hover slide-icons"/>
    </Tooltip>
    return slide.isFetching ? getBusyComponent() : icon;
}


export const getDeleteComponent = (slide, handleDelete) => {

    if (!hasPermissions([aclPermissions.delete_slide])) return null

    let icon = <DeleteFilled
        onClick={(event) => event.stopPropagation()}
        style={{textAlign: 'center'}}
        className="icon-hover slide-icons"/>
    return slide.isFetching ? getBusyComponent() : <Popconfirm key={0}
                                                               title="Are you sure delete this slide?"
                                                               onConfirm={(event) => {
                                                                   event.stopPropagation();
                                                                   handleDelete(slide, true);
                                                               }}
                                                               disabled={!hasPermissions([aclPermissions.delete_slide])}
                                                               okText="Yes"
                                                               palcement="topRight"
                                                               style={{padding: 20}}
                                                               cancelText="No"
                                                               onCancel={(event) => {
                                                                   event.stopPropagation();
                                                               }}>
        {icon}
    </Popconfirm>
}


export const getTilingComponent = (slide, dispatch) => {

    if (isCloud()) return null;

    let tilingStatus = slide.tiling_status;

    let tilingComponent = null;

    if (slide.isFetching) {
        return getBusyComponent();
    }

    if (slideStatusCategory(slide.status) === slideStatusEnum.VIEWER_READY) {
        if (cookie.loadAll().superuser === "true") {
            if (tilingStatus === 0) {
                tilingComponent = <Tooltip placement="bottomRight" title="Start Tiling"
                                           className="icon-hover slide-icons"
                                           style={{
                                               display: "flex",
                                               flexDirection: "row",
                                               justifyContent: "center",
                                               alignItems: "center",
                                           }}>
                    <PlayCircleOutlined
                        onClick={(event) => {
                            event.stopPropagation();
                            dispatch(startTiling(slide.morphle_id, slide.id));
                        }}
                        type="build"
                    />
                </Tooltip>
            } else if (tilingStatus === 2) {
                tilingComponent = <Tooltip placement="bottomRight" title="Tiled">
                    <BuildOutlined
                        onClick={(event) => {
                            event.stopPropagation();
                            openEndPoint(`/gamma/${slide.id}?#?tileMapEnable=true&topApp=${tileViewApp.id}`, false, true);
                        }}
                        type="build"
                        className="icon-hover slide-icons"
                    />
                </Tooltip>;
            } else if (tilingStatus === 100) {
                tilingComponent =
                    <div key="2">Error occured while Tiling. Check Jenkins 'raw_tiling'.</div>
            } else if (tilingStatus === 1) {
                tilingComponent = <CircularProgressWithLabel key={"2"} value={slide.tiling_percent}/>
            }
        }
    }
    return tilingComponent;
}

const exportDownloadHref = (slideId, exportFormat) => {
    const url = getStateAwareUrl(
        `/api/slides/${slideId}/export/download/?export_format=${encodeURIComponent(exportFormat)}`
    );
    return url.startsWith('http') ? url : (window.location.origin + url);
};

const startExportFileDownload = async (event, slideId, exportFormat) => {
    if (event) {
        event.stopPropagation();
    }
    const href = exportDownloadHref(slideId, exportFormat);
    try {
        const probe = await fetch(href, {
            method: 'GET',
            credentials: 'include',
            headers: {Range: 'bytes=0-0'},
        });
        if (probe.body) {
            probe.body.cancel();
        }
        if (probe.status !== 200 && probe.status !== 206) {
            message.error(probe.status === 403
                ? 'You do not have permission to download this export'
                : 'This export file is no longer available');
            return false;
        }
    } catch (e) {
        message.error('Could not download the export');
        return false;
    }
    const link = document.createElement('a');
    link.href = href;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
};

const ExportFormatDownload = ({slide}) => {
    const [formats, setFormats] = useState(null);
    const [menuVisible, setMenuVisible] = useState(false);
    const checkId = useRef(0);

    const refresh = () => {
        return get(`/api/slides/${slide.id}/export/downloads/`)
            .then((data) => {
                const next = Array.isArray(data && data.formats) ? data.formats : [];
                setFormats(next);
                return next;
            })
            .catch(() => {
                setFormats([]);
                return [];
            });
    };

    useEffect(() => {
        refresh();
    }, [slide.id]);

    if (!formats || formats.length === 0) {
        return null;
    }

    const menu = <Menu>
        {formats.map((item) => <Menu.Item key={item.format}>
            <Button onClick={(event) => {
                startExportFileDownload(event, slide.id, item.format).then((ok) => {
                    if (!ok) {
                        refresh();
                    }
                });
            }}>
                {item.label}
            </Button>
        </Menu.Item>)}
    </Menu>;

    return <Dropdown
        overlay={menu}
        trigger={['hover']}
        visible={menuVisible}
        onVisibleChange={(visible) => {
            if (!visible) {
                checkId.current += 1;
                setMenuVisible(false);
                return;
            }
            const id = checkId.current + 1;
            checkId.current = id;
            setMenuVisible(false);
            refresh().then((next) => {
                if (checkId.current !== id) {
                    return;
                }
                setMenuVisible(next.length > 0);
            });
        }}>
        <DownloadOutlined
            onClick={(event) => event.stopPropagation()}
            className="icon-hover slide-icons"/>
    </Dropdown>;
};

export const getExportComponent = (slide, dispatch) => {

    if (isCloud()) return null;

    let exportStatus = slide.export_status;

    let exportComponent = null;

    const canExport = hasPermissions([aclPermissions.export_slide]);
    const canDownload = hasPermissions([aclPermissions.download_slide_export]);

    if (!canExport && !canDownload) {
        return null
    }

    if (slide.isFetching) {
        return getBusyComponent();
    }

    if (slideStatusCategory(slide.status) === slideStatusEnum.VIEWER_READY) {

        const exportActionMenu = <Menu>
            {exportStatus === slideExportStatus.QUEUED && <Menu.Item>
                <Button onClick={(event) => {
                    event.stopPropagation();
                    dispatch(prioritizeExport(slide.morphle_id, slide.name, slide.id));
                }}>Prioritize Export</Button>
            </Menu.Item>}
            <Menu.Item>
                <Button onClick={(event) => {
                    event.stopPropagation();
                    dispatch(cancelExport(slide.morphle_id, slide.name, slide.id));
                }}>Cancel Export</Button>
            </Menu.Item>
        </Menu>

        if (exportStatus === slideExportStatus.DONE) {
            exportComponent = <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}>
                {canDownload && <ExportFormatDownload slide={slide}/>}
                {canExport && <Tooltip placement="bottomRight" title="Exported (Click to Redo)">
                    <FileDoneOutlined
                        onClick={(event) => {
                            event.stopPropagation();
                            dispatch(startExport(slide.morphle_id, slide.name, slide.id));
                        }}
                        className="icon-hover slide-icons"/>
                </Tooltip>}
            </span>
        } else if (!canExport) {
            exportComponent = null;
        } else if (exportStatus === slideExportStatus.NOT_DONE) {
            exportComponent = <Tooltip placement="bottomRight" title="Export">
                <ExportOutlined
                    onClick={(event) => {
                        event.stopPropagation();
                        dispatch(startExport(slide.morphle_id, slide.name, slide.id));
                    }}
                    className="icon-hover slide-icons"/>
            </Tooltip>
        } else if (exportStatus === slideExportStatus.QUEUED || exportStatus === slideExportStatus.QUEUED_PRIORITY) {
            exportComponent = <Dropdown key="export-options" overlay={exportActionMenu} placement="topLeft">
                <ClockCircleOutlined
                    style={{
                        cursor: 'default',
                        color: exportStatus === slideExportStatus.QUEUED_PRIORITY ? '#ffc300' : '#1890ff'
                    }}
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                    className="icon-hover slide-icons"/>
            </Dropdown>
        } else if (exportStatus >= slideExportStatus.ERROR) {
            exportComponent =
                <Tooltip placement="bottomRight" title="Export Error (Click to retry/Contact morphle support)">
                    <FileExclamationTwoTone
                        twoToneColor='red'
                        onClick={(event) => {
                            event.stopPropagation();
                            dispatch(startExport(slide.morphle_id, slide.name, slide.id));
                        }}
                        className="icon-hover slide-icons"/>
                </Tooltip>
        } else if (exportStatus === slideExportStatus.IN_PROGRESS) {
            exportComponent = <CircularProgressWithLabel key={"2"} value={slide.export_percent}/>
        }
    }

    return exportComponent;
}

/**
 * Get the share component for the slide
 * @param slide - slide object
 * @param dispatch - dispatch function
 * @returns {null}
 */
export const getShareComponent = (slide, dispatch) => {
    let shareComponent = null;
    if ((slideStatusCategory(slide.status) === slideStatusEnum.VIEWER_READY) && hasPermissions([aclPermissions.share_slide])) {
        shareComponent = <Tooltip placement="bottomRight" title="Share">
            <UsergroupAddOutlined
                onClick={(event) => {
                    event.stopPropagation();
                    dispatch(addSlidesToShare(slide.id));
                }}
                className="icon-hover slide-icons"/>
        </Tooltip>
    }

    return shareComponent;
}

export const getObjectiveComponent = (slide, type) => {
    let objectiveComponent = undefined;
    if (slide.objective_type.toLowerCase() === objectiveType.HUNDRED_X.toLowerCase()) {
        objectiveComponent = slide.unread && type === slideViewType.CASEVIEW ? <b>100x</b> : <span>100x</span>;
    } else if (slide.objective_type.toLowerCase() === objectiveType.FORTY_X.toLowerCase()) {
        objectiveComponent = slide.unread && type === slideViewType.CASEVIEW ? <b>40x</b> : <span>40x</span>;
    } else if (slide.objective_type.toLowerCase() === objectiveType.TWENTY_X.toLowerCase()) {
        objectiveComponent = slide.unread && type === slideViewType.CASEVIEW ? <b>20x</b> : <span>20x</span>;
    }
    return objectiveComponent;
}

export const encodeStringWithNewLine = (string) => {
    return string.replace(/\n/g, '&nbnl');
};

export const decodeStringWithNewLine = (string) => {
    return string.replace(/&nbnl/g, '\n');
};

export const getLoadingComponent = () => {
    return <Spin tip="Loading..." /*delay={500}*/>
        <Alert
            message="Your data is loading..."
            description="Please wait..."
            type="info"
        />
    </Spin>
}

export const getErrorComponent = (message = "") => {
    return <Alert
        message="Error"
        description={message.length > 0 ? message : "Some error occured. Please contact admin."}
        type="error"
        showIcon
    />
}

export const assignCaseComponent = (slide, handleAssignCase) => {
    return slide.isFetching ? getBusyComponent() :
        slide.case == null ?
            <Tooltip placement="bottomRight" title="Barcode not detected properly">
                <Button
                    type="danger"
                    onClick={(event) => {
                        event.stopPropagation();
                        handleAssignCase(slide, !slide.barcode_failed);
                    }}
                    ghost
                >
                    <ExclamationCircleOutlined /*theme='filled'*/ style={{ color: 'red', fontSize: '15px' }} />
                    Add to {getCaseNamePlaceholder()}
                </Button>
            </Tooltip> : null;
}

export const getUnauthorizedComponent = (message = "") => {
    return <Alert
        message="Unauthorized"
        description={message.length > 0 ? message : "You are not allowed to view this page. or, try reload page (Ctrl + Shift + R)"}
        type="error"
        showIcon
    />
}

export const getScreenshotCarousel = (screenshots) => {

    const slider = createRef()

    return <Row align={"middle"} justify={"center"}>
        <Col span={1} style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '679px',
            cursor: 'pointer'
        }}
             onClick={(e) => {
                 e.stopPropagation();
                 slider.current.prev();
             }}>
            <LeftOutlined/>
        </Col>
        <Col span={22}>
            <Carousel ref={ref => {
                slider.current = ref;
                console.log(ref, slider)
            }}>
                {screenshots.map((screenshot) => {
                    return <div>
                        <img src={screenshot} alt={"Screenshot"} style={{width: '1328px', height: '679px'}}/>
                    </div>
                })}
            </Carousel>
        </Col>
        <Col span={1} style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '679px',
            cursor: 'pointer'
        }}
             onClick={(e) => {
                 e.stopPropagation();
                 slider.current.next();
             }}>
            <RightOutlined/>
        </Col>
    </Row>
}

export const getArchiveComponent = (slide) => {
    if (!ArchiveConstants.ACTIVE) return null;
    let title, color;
    switch (slide.archive_status) {
        case ArchiveConstants.STATUS.ARCHIVED:
            title = "Slide is archived, click the button to send retrieval request";
            color = 'yellow';
            break;
        case ArchiveConstants.STATUS.RETRIEVAL_REQUESTED:
            title = "Slide retrieval request is sent";
            color = 'violet';
            break;
        case ArchiveConstants.STATUS.RETRIEVING:
            title = "Slide retrieval in progress";
            color = 'mediumvioletred';
            break;
        case ArchiveConstants.STATUS.RETRIEVED:
            title = `Slide is retrieved and available till ${getSlideArchivalDate(slide)}`;
            color = 'blue';
            break;
        default:
            title = `Available for viewing until archival at ${getSlideArchivalDate(slide)}`;
            color = 'green';
            break;
    }
    let retrieve_menu = <Menu>
        <Menu.Item>
            <Button onClick={() => patchSlide(Object.assign(slide, {
                archive_status: ArchiveConstants.STATUS.RETRIEVAL_REQUESTED
            })).then(() => message.success("Slide Retrieval request sent"))}>
                Retrieve Slide
            </Button>
        </Menu.Item>
    </Menu>;

    return slide.archive_status === ArchiveConstants.STATUS.ARCHIVED ?
        <Dropdown key="retrieve-slide" overlay={retrieve_menu} placement="bottomCenter">
            <Tooltip title={title}>
                <FaCircle style={{color, marginTop: '7px'}}/>
            </Tooltip>
        </Dropdown> :
        <Tooltip title={title}>
            <FaCircle style={{color, marginTop: '7px'}}/>
        </Tooltip>;
}

export const customizeRenderEmpty = () => (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={"No slides found for given filter."}></Empty>
);